// src/pages/RecordMeeting.js
// Meeting recording interface + edit mode for completed meetings

import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore/lite';
import { db } from '../firebase/config';
import {
  ALL_CODES, SLASH_CODES, BACKTICK_CODES, PROCESS_CODE_PAIRS,
  parseShortcodes, formatDuration, extractTopic, extractTaggedTexts,
} from '../utils/shortcodes';
import { computeActiveDurationMs } from '../utils/metrics';
import styles from './RecordMeeting.module.css';
import PageLoading from '../components/PageLoading';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function parseSpeaker(text, attendees) {
  // Primary: \u200B-terminated match (inserted by dropdown picker)
  const termMatch = text.match(/@([^\u200B\n]+)\u200B/);
  if (termMatch) {
    const q = termMatch[1].toLowerCase().trim();
    return attendees.find(a =>
      getDisplayName(a).toLowerCase().includes(q) ||
      a.name.toLowerCase().includes(q)
    ) || null;
  }
  // Fallback: typed @name \u2014 try longest prefix match against attendees (handles multi-word names)
  const raw = text.match(/@([^\n@\u200B]+)/)?.[1];
  if (!raw) return null;
  const words = raw.toLowerCase().trim().split(/\s+/);
  for (let n = words.length; n >= 1; n--) {
    const q = words.slice(0, n).join(' ');
    const found = attendees.find(a =>
      getDisplayName(a).toLowerCase().includes(q) ||
      a.name.toLowerCase().includes(q)
    );
    if (found) return found;
  }
  return null;
}

function getDisplayName(attendee) {
  if (!attendee) return '';
  if (attendee.displayName) return attendee.displayName;
  const name = attendee.name || '';
  return name.includes('@') ? name.split('@')[0] : name;
}

function displayName(name) {
  return name?.split('@')[0] || name;
}


const AVATAR_COLORS = [
  '#2a3d5b', '#5f7942', '#cba43a', '#922b3f',
  '#7209b7', '#4cc9f0', '#e76f51', '#4361ee',
];

function avatarColor(name = '') {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) & 0xffff;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
}

// ─── Break marker row (rendered in the entry feed) ───────────────────────────

function BreakMarkerRow({ entry }) {
  const startStr = entry.startTime
    ? new Date(entry.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : '';
  const duration = entry.startTime && entry.endTime
    ? formatDuration(entry.endTime - entry.startTime)
    : '';
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 10,
      padding: '6px 12px', borderBottom: '1px solid var(--border)',
    }}>
      <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
      <span style={{ fontSize: '0.72rem', color: 'var(--warning)', fontWeight: 600, whiteSpace: 'nowrap' }}>
        ⏸ Break {startStr}{duration && ` · ${duration}`}
      </span>
      <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
    </div>
  );
}

// ─── Tag chip ─────────────────────────────────────────────────────────────────

function TagChip({ code, def }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 3,
      padding: '2px 7px', borderRadius: 100,
      background: def.color + '1a', color: def.color,
      fontSize: '0.68rem', fontWeight: 700,
      border: `1px solid ${def.color}33`,
      whiteSpace: 'nowrap',
    }}>
      {def.emoji} {def.label}
    </span>
  );
}

// ─── Live Editable Entry Row (used during active recording) ──────────────────

function LiveEntryRow({ entry, index, attendees, onUpdate, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(entry.text || '');
  const [speakerName, setSpeakerName] = useState(entry.speakerName || '');
  const codes = parseShortcodes(entry.text || '');
  const color = avatarColor(entry.speakerName);

  const cleanText = (entry.text || '')
    .replace(/@[^\u200B\n]+\u200B|@[\w.]+/g, '')
    .replace(/\$[^\u200B$\n]+\u200B|\$[^$]+\$/g, '')
    .replace(/#[^#]+#/g, '')
    .replace(/[`/][-\w]+/g, '')
    .trim();

  const save = () => {
    const attendee = attendees.find(a => a.name === speakerName);
    const speakerDisplayName = attendee?.displayName || displayName(speakerName);
    onUpdate(index, { ...entry, text, speakerName, speakerDisplayName });
    setEditing(false);
  };

  const cancel = () => {
    setText(entry.text || '');
    setSpeakerName(entry.speakerName || '');
    setEditing(false);
  };

  if (editing) {
    return (
      <div className={styles.liveEditBlock}>
        <div className={styles.liveEditSpeakerRow}>
          <select
            value={speakerName}
            onChange={e => setSpeakerName(e.target.value)}
            className={styles.liveEditSpeakerSelect}
          >
            {attendees.map(a => (
              <option key={a.id} value={a.name}>{getDisplayName(a)}</option>
            ))}
            {!attendees.find(a => a.name === speakerName) && speakerName && (
              <option value={speakerName}>{displayName(speakerName)}</option>
            )}
          </select>
          <span className={styles.liveEditTimestamp}>
            {entry.startTime ? new Date(entry.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
          </span>
        </div>
        <textarea
          autoFocus
          value={text}
          onChange={e => setText(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); save(); }
            if (e.key === 'Escape') cancel();
          }}
          rows={2}
          className={styles.liveEditTextarea}
        />
        <div className={styles.liveEditActions}>
          <button onClick={() => onDelete(index)} className={styles.btnDangerSm}>Delete</button>
          <div className={styles.editActionsRight}>
            <button onClick={cancel} className={styles.btnSecondarySm}>Cancel</button>
            <button onClick={save} className={styles.btnNavySm}>Save</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div onClick={() => setEditing(true)} className={styles.liveEntryRow}>
      <div
        className={styles.entryAvatar}
        style={{ background: color }}
      >
        {(entry.speakerDisplayName || displayName(entry.speakerName))?.charAt(0)?.toUpperCase() || '?'}
      </div>

      <div className={styles.entryContent}>
        <div className={styles.entryHeaderRow}>
          <span className={styles.entrySpeakerName} style={{ color }}>
            {entry.speakerDisplayName || displayName(entry.speakerName)}
          </span>
          {entry.topic && (
            <span className={styles.entryTopicTag}># {entry.topic}</span>
          )}
          {entry.startTime && (
            <span className={styles.entryTimestamp}>
              {new Date(entry.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              {' '}✎
            </span>
          )}
        </div>

        <div className={styles.entryText}>
          {cleanText || <em className={styles.entryEmptyText}>no text</em>}
        </div>

        {codes.length > 0 && (
          <div className={styles.entryCodesRow}>
            {codes.slice(0, 4).map((c, i) => (
              <TagChip key={i} code={c.code} def={c.definition} />
            ))}
            {codes.length > 4 && (
              <span className={styles.entryCodeExtra}>+{codes.length - 4}</span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Mention Dropdown ─────────────────────────────────────────────────────────

function MentionDropdown({ suggestions, activeIndex, onSelect }) {
  if (suggestions.length === 0) return null;
  return (
    <div className={styles.dropdown}>
      {suggestions.map((a, i) => (
        <div
          key={a.id}
          onMouseDown={e => { e.preventDefault(); onSelect(a); }}
          className={styles.dropdownItem}
          style={{
            background: i === activeIndex ? 'var(--olive-glow)' : 'transparent',
            borderBottom: i < suggestions.length - 1 ? '1px solid var(--border)' : 'none',
          }}
        >
          <div
            className={styles.dropdownAvatar}
            style={{ background: avatarColor(a.name) }}
          >
            {getDisplayName(a)?.charAt(0)?.toUpperCase()}
          </div>
          <span className={styles.dropdownName}>{getDisplayName(a)}</span>
        </div>
      ))}
      <div className={styles.dropdownHint}>↑↓ navigate · Enter or Tab to select</div>
    </div>
  );
}

// ─── Shortcode Panel ──────────────────────────────────────────────────────────

function ShortcodePanel({ currentTopic, mobileOpen, onClose, fixedLeft }) {
  const sections = [
    { label: 'Content', codes: SLASH_CODES, filter: 'content' },
    { label: 'Behavior', codes: SLASH_CODES, filter: 'behavior' },
    { label: 'Right Chemistry', codes: BACKTICK_CODES, filter: 'genius' },
    { label: 'Consulting', codes: SLASH_CODES, filter: 'consulting' },
    { label: 'Process', type: 'process' },
  ];

  const fixedStyle = fixedLeft != null && !mobileOpen
    ? { position: 'fixed', top: 24, left: fixedLeft, width: 220, zIndex: 10 }
    : {};

  return (
    <div className={`shortcode-panel-desktop${mobileOpen ? ' shortcode-panel-mobile-open' : ''} ${styles.shortcodePanel}`} style={fixedStyle}>
      {mobileOpen && (
        <button onClick={onClose} className={styles.panelCloseBtn}>✕</button>
      )}

      <div className={styles.panelTitle}>Shortcodes</div>

      <div className={styles.panelSection}>
        <div className={styles.panelSectionLabel}>Meeting Structure</div>
        <div className={styles.panelContent}>
          <div className={styles.panelMuted}>
            <span className={styles.panelBold}>#TopicName#</span><br />
            <span className={styles.panelSmHint}>Define a topic</span>
          </div>
          <div style={{ color: 'rgba(255,255,255,0.5)', marginBottom: 4 }}>
            <span className={styles.panelBold}>"quote"</span> good · <span className={styles.panelBold}>-"quote"</span> bad
          </div>
        </div>
        {currentTopic && (
          <div className={styles.panelStateBlock}>
            <div className={styles.panelStateLabel}>Current Topic:</div>
            <div className={styles.panelCurrentTopic}>#{currentTopic}</div>
          </div>
        )}
      </div>

      {sections.map(({ label, codes, filter, type }) => {
        if (type === 'process') {
          return (
            <div key={label} className={styles.panelCodeGroup}>
              <div className={styles.panelSectionLabel}>{label}</div>
              {PROCESS_CODE_PAIRS.map(({ base, label: pairLabel, emoji }) => (
                <div key={base} className={styles.panelCodeRow}>
                  <span style={{ display: 'flex', gap: 3, flexShrink: 0 }}>
                    <code className={styles.panelCodeBadge} style={{ color: '#00897b' }}>{base}</code>
                    <code className={styles.panelCodeBadge} style={{ color: '#c62828' }}>{base}-</code>
                  </span>
                  <span className={styles.panelCodeLabel}>{emoji} {pairLabel}</span>
                </div>
              ))}
              <div style={{ fontSize: '0.62rem', color: 'rgba(255,255,255,0.4)', marginTop: 4 }}>
                + = observed · - = missed
              </div>
            </div>
          );
        }
        const filtered = Object.entries(codes).filter(([, def]) => def.category === filter);
        if (filtered.length === 0) return null;
        return (
          <div key={label} className={styles.panelCodeGroup}>
            <div className={styles.panelSectionLabel}>{label}</div>
            {filtered.map(([code, def]) => (
              <div key={code} className={styles.panelCodeRow}>
                <code className={styles.panelCodeBadge} style={{ color: def.color }}>
                  {code}
                </code>
                <span className={styles.panelCodeLabel}>{def.emoji} {def.label}</span>
              </div>
            ))}
          </div>
        );
      })}

      <div className={styles.panelFooter}>
        <div className={styles.panelFooterText}>
          <span className={styles.panelFooterBold}>@name</span> to tag speaker<br />
          <span className={styles.panelFooterBold}>Enter</span> to commit · click entry to edit
        </div>
      </div>
    </div>
  );
}

// ─── Edit Mode: Editable Entry Row ────────────────────────────────────────────

function EditableEntryRow({ entry, index, attendees, onUpdate, onDelete, onAddBelow }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(entry.text || '');
  const [speakerName, setSpeakerName] = useState(entry.speakerName || '');
  const [topic, setTopic] = useState(entry.topic || '');
  const color = avatarColor(speakerName);
  const codes = parseShortcodes(text);

  useEffect(() => {
    if (!editing) {
      setTopic(entry.topic || '');
    }
  }, [entry.topic, editing]);

  const save = () => {
    const attendee = attendees.find(a => a.name === speakerName);
    const speakerDisplayName = attendee?.displayName || displayName(speakerName);
    onUpdate(
      index,
      { ...entry, text, speakerName, speakerDisplayName, topic: topic || null },
      { oldSection: entry.section, oldTopic: entry.topic }
    );
    setEditing(false);
  };

  return (
    <div style={{
      borderBottom: '1px solid var(--border)',
      background: editing ? 'var(--bg-soft)' : 'transparent',
      transition: 'background 0.15s',
    }}>
      {editing ? (
        <div className={styles.editBlockLg}>
          <div className={styles.speakerLabelBlock}>
            <label className={styles.speakerFieldLabel}>Speaker</label>
            <select
              value={speakerName}
              onChange={e => setSpeakerName(e.target.value)}
              className={styles.editSpeakerSelect}
            >
              {attendees.map(a => (
                <option key={a.id} value={a.name}>{getDisplayName(a)}</option>
              ))}
              {!attendees.find(a => a.name === speakerName) && speakerName && (
                <option value={speakerName}>{displayName(speakerName)}</option>
              )}
            </select>
          </div>
          <div style={{ marginBottom: 6 }}>
            <label className={styles.speakerFieldLabel}>Topic</label>
            <input
              type="text"
              value={topic}
              onChange={e => setTopic(e.target.value)}
              placeholder="e.g. Budget Review"
              className={styles.editSpeakerSelect}
            />
          </div>
          <textarea
            autoFocus
            value={text}
            onChange={e => setText(e.target.value)}
            rows={3}
            className={styles.editTextareaLg}
          />
          <div className={styles.editActionsLg}>
            <button onClick={() => onDelete(index)} className={styles.btnDangerMd}>
              Delete entry
            </button>
            <div className={styles.editActionsRightLg}>
              <button
                onClick={() => {
                  setText(entry.text || '');
                  setSpeakerName(entry.speakerName || '');
                  setTopic(entry.topic || '');
                  setEditing(false);
                }}
                className={styles.btnSecondaryMd}
              >
                Cancel
              </button>
              <button onClick={save} className={styles.btnNavyMd}>Save</button>
            </div>
          </div>
        </div>
      ) : (
        <div className={styles.editableEntryViewRow} onClick={() => setEditing(true)}>
          <div className={styles.entryAvatar} style={{ background: color }}>
            {displayName(speakerName)?.charAt(0)?.toUpperCase() || '?'}
          </div>
          <div className={styles.entryContent}>
            <div className={styles.entryHeaderRow}>
              <span className={styles.entrySpeakerName} style={{ color }}>{displayName(speakerName)}</span>
              {entry.topic && <span className={styles.entryTopicTag}># {entry.topic}</span>}
              <span className={styles.entryEditHint}>click to edit</span>
            </div>
            <div className={styles.entryText}>
              {text || <em className={styles.entryEmptyText}>no text</em>}
            </div>
            {codes.length > 0 && (
              <div className={styles.entryCodesRow}>
                {codes.slice(0, 4).map((c, i) => <TagChip key={i} code={c.code} def={c.definition} />)}
                {codes.length > 4 && <span className={styles.entryCodeExtra}>+{codes.length - 4}</span>}
              </div>
            )}
          </div>
        </div>
      )}
      {!editing && (
        <div
          onClick={e => { e.stopPropagation(); onAddBelow(index); }}
          className={styles.addEntryHint}
        >
          + add entry here
        </div>
      )}
    </div>
  );
}

// ─── Edit Mode View ───────────────────────────────────────────────────────────

function EditModeView({ meeting, entries, setEntries, navigate, meetingId, onContinueMeeting }) {
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const attendees = meeting.attendees || [];

  const handleUpdate = (index, updated, meta = {}) => {
    setEntries(prev => {
      const oldTopic = meta.oldTopic ?? null;
      const oldSection = meta.oldSection ?? null;
      // Topic ripples even when the entry had no topic before (null → value)
      const topicChanged = updated.topic && updated.topic !== oldTopic;
      const sectionChanged = meta.oldSection && updated.section && updated.section !== meta.oldSection;
      const result = [...prev];
      result[index] = updated;
      if (topicChanged || sectionChanged) {
        let stopTopic = !topicChanged;
        let stopSection = !sectionChanged;
        for (let i = index + 1; i < result.length; i++) {
          const e = result[i];
          if (!stopTopic) {
            // Ripple the new topic down until a new section or a different topic is hit
            if ((e.section ?? null) !== oldSection || (e.topic ?? null) !== oldTopic) {
              stopTopic = true;
            } else {
              result[i] = { ...result[i], topic: updated.topic };
            }
          }
          if (!stopSection) {
            if (e.section === meta.oldSection) {
              result[i] = { ...result[i], section: updated.section };
            } else if (e.section !== null) {
              stopSection = true;
            }
          }
          if (stopTopic && stopSection) break;
        }
      }
      return result;
    });
  };
  const handleDelete = (index) => {
    if (!window.confirm('Delete this entry?')) return;
    setEntries(prev => prev.filter((_, i) => i !== index));
  };
  const handleAddBelow = (index) => {
    const newEntry = {
      id: Date.now().toString(),
      speakerName: '',
      speakerId: null,
      text: '',
      startTime: Date.now(), endTime: Date.now(),
      section: entries[index]?.section || null, topic: entries[index]?.topic || null,
    };
    setEntries(prev => [...prev.slice(0, index + 1), newEntry, ...prev.slice(index + 1)]);
  };

  const handleSaveAndContinue = async () => {
    setSaving(true);
    try {
      const participantFeedback = buildParticipantFeedback(entries, attendees);
      await updateDoc(doc(db, 'meetings', meetingId), {
        entries, participantFeedback,
        updatedAt: serverTimestamp(),
      });
      navigate(`/meetings/${meetingId}/inventory`);
    } catch (err) {
      console.error(err);
      alert('Failed to save. Please try again.');
      setSaving(false);
    }
  };

  const handleSaveDraft = async () => {
    try {
      await updateDoc(doc(db, 'meetings', meetingId), { entries, updatedAt: serverTimestamp() });
      setLastSaved(new Date());
    } catch (err) { console.error(err); }
  };

  return (
    <div className={styles.pageLayout}>
      <div className={styles.mainCol}>
        <div className={styles.headerSmMb}>
          <button onClick={() => navigate(`/meetings/${meetingId}/report`)} className={`link ${styles.backLink}`}>
            ← Back to report
          </button>
          <div className={styles.headerRight}>
            {lastSaved && <span className={styles.lastSaved}>Saved {lastSaved.toLocaleTimeString()}</span>}
            <button className={`btn btn-secondary ${styles.btnSmFont}`} onClick={handleSaveDraft}>Save Draft</button>
            {onContinueMeeting && (
              <button className={`btn btn-secondary ${styles.btnSmFont}`} onClick={onContinueMeeting}>▶ Continue Meeting</button>
            )}
            <button className="btn btn-primary" onClick={handleSaveAndContinue} disabled={saving}>
              {saving ? <><span className="spinner" /> Saving...</> : 'Save & Continue to Inventory →'}
            </button>
          </div>
        </div>

        <div className={styles.editBanner}>
          <span className={styles.bannerIcon}>✏️</span>
          <div><strong>Edit Mode</strong> — click any entry to edit. Add entries with "+ add entry here". Save to continue to the inventory.</div>
        </div>

        <div className={styles.meetingInfo}>
          <h2 className={styles.meetingTitle}>{meeting.name}</h2>
          <div className={styles.meetingMeta}>{meeting.teamName} · {entries.length} entries</div>
        </div>

        <div className={styles.feedContainer}>
          {entries.length === 0 && (
            <div className={styles.emptyState}>
              <p className={styles.emptyStateText}>No entries yet. Use "+ add entry here" to get started.</p>
            </div>
          )}
          {entries.map((entry, i) => (
            entry.type === 'break'
              ? <BreakMarkerRow key={entry.id || i} entry={entry} />
              : <EditableEntryRow key={entry.id || i} entry={entry} index={i} attendees={attendees} onUpdate={handleUpdate} onDelete={handleDelete} onAddBelow={handleAddBelow} />
          ))}
          <div
            onClick={() => handleAddBelow(entries.length - 1)}
            className={styles.addEntryEnd}
            style={{ borderTop: entries.length > 0 ? '1px solid var(--border)' : 'none' }}
          >
            + add entry at end
          </div>
        </div>

        <div className={styles.bottomActions}>
          <button className={`btn btn-primary ${styles.bottomSaveBtn}`} onClick={handleSaveAndContinue} disabled={saving}>
            {saving ? <><span className="spinner" /> Saving...</> : 'Save & Continue to Inventory →'}
          </button>
        </div>
      </div>
      <ShortcodePanel currentTopic={null} mobileOpen={false} onClose={() => {}} />
    </div>
  );
}

// ─── Build participant feedback from /c comments ──────────────────────────────

function buildParticipantFeedback(entries, attendees) {
  const feedback = {};

  // Pre-seed all attendees with empty strings
  attendees.forEach(a => {
    feedback[a.name] = { displayName: getDisplayName(a), notes: '' };
  });

  // Extract /c comments and append to the tagged speaker's notes
  entries.forEach(entry => {
    if (entry.type === 'break') return;
    const comments = extractTaggedTexts(entry.text || '', '/c');
    if (!comments.length) return;

    const speakerName = entry.speakerName;
    if (!feedback[speakerName]) {
      feedback[speakerName] = { displayName: entry.speakerDisplayName || displayName(speakerName), notes: '' };
    }
    const existing = feedback[speakerName].notes;
    feedback[speakerName].notes = [existing, ...comments].filter(Boolean).join('\n');
  });

  return feedback;
}

// ─── Main Recording Page ──────────────────────────────────────────────────────

export default function RecordMeeting() {
  const { meetingId } = useParams();
  const navigate = useNavigate();

  const [meeting, setMeeting] = useState(null);
  const [loading, setLoading] = useState(true);
  const [entries, setEntries] = useState([]);
  const [currentText, setCurrentText] = useState('');
  const [currentEntryStart, setCurrentEntryStart] = useState(null);
  const [meetingStartTime, setMeetingStartTime] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [pauseTime, setPauseTime] = useState(null);
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [showPanel, setShowPanel] = useState(false);
  const [showAddAttendee, setShowAddAttendee] = useState(false);
  const [newAttendeeName, setNewAttendeeName] = useState('');

  // @mention state
  const [mentionQuery, setMentionQuery] = useState('');
  const [showMention, setShowMention] = useState(false);
  const [mentionIndex, setMentionIndex] = useState(0);
  const [resolvedSpeaker, setResolvedSpeaker] = useState(null);

  // Topic tracking
  const [currentTopic, setCurrentTopic] = useState('Topic 1');

  const inputRef = useRef(null);
  const timerRef = useRef(null);
  const autoSaveRef = useRef(null);
  const entriesRef = useRef(entries);
  entriesRef.current = entries;
  const isRunningRef = useRef(false);
  const isPausedRef = useRef(false);
  useEffect(() => { isRunningRef.current = isRunning; }, [isRunning]);
  useEffect(() => { isPausedRef.current = isPaused; }, [isPaused]);
  const panelAnchorRef = useRef(null);
  const [panelLeft, setPanelLeft] = useState(null);

  useEffect(() => {
    const measure = () => {
      if (panelAnchorRef.current) {
        setPanelLeft(panelAnchorRef.current.getBoundingClientRect().left);
      }
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  useEffect(() => {
    loadMeeting();
    return () => { clearInterval(timerRef.current); clearInterval(autoSaveRef.current); };
  }, [meetingId]);

  useEffect(() => {
    if (!isRunning) return;
    autoSaveRef.current = setInterval(saveDraft, 30000);
    return () => clearInterval(autoSaveRef.current);
  }, [isRunning]);

  useEffect(() => {
    const handlePageHide = () => {
      if (isRunningRef.current && !isPausedRef.current) {
        localStorage.setItem('rm_autopause', JSON.stringify({ meetingId, pauseStart: Date.now() }));
      }
    };
    window.addEventListener('pagehide', handlePageHide);
    return () => window.removeEventListener('pagehide', handlePageHide);
  }, [meetingId]);

  async function loadMeeting() {
    try {
      const snap = await getDoc(doc(db, 'meetings', meetingId));
      if (!snap.exists()) return navigate('/meetings');
      const data = { id: snap.id, ...snap.data() };
      setMeeting(data);
      if (data.entries?.length > 0) {
        setEntries(data.entries);
        const lastEntry = data.entries[data.entries.length - 1];
        if (lastEntry.topic) setCurrentTopic(lastEntry.topic);
      }
      if (data.meetingStartTime) {
        setMeetingStartTime(data.meetingStartTime);
        if (data.status === 'active') {
          // Recover from auto-pause saved on page exit
          const savedPause = localStorage.getItem('rm_autopause');
          if (savedPause) {
            try {
              const { meetingId: savedId, pauseStart } = JSON.parse(savedPause);
              if (savedId === meetingId) {
                const lastEntry = data.entries?.[data.entries.length - 1];
                const breakEntry = {
                  id: `break-${Date.now()}`,
                  type: 'break',
                  startTime: pauseStart,
                  endTime: Date.now(),
                  text: '', speakerName: '', speakerDisplayName: '', speakerId: null,
                  section: lastEntry?.section ?? null,
                  topic: lastEntry?.topic ?? null,
                };
                data.entries = [...(data.entries || []), breakEntry];
                await updateDoc(doc(db, 'meetings', meetingId), { entries: data.entries, updatedAt: serverTimestamp() });
              }
            } catch (e) { /* ignore malformed localStorage */ }
            localStorage.removeItem('rm_autopause');
          }

          setIsRunning(true);
          if (data.entries?.length > 0) setEntries(data.entries);
          const totalBreakMs = (data.entries || [])
            .filter(e => e.type === 'break')
            .reduce((sum, e) => sum + (e.endTime && e.startTime ? e.endTime - e.startTime : 0), 0);
          const adjustedStart = data.meetingStartTime + totalBreakMs;
          timerRef.current = setInterval(() => setElapsed(Date.now() - adjustedStart), 1000);
        }
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  const startMeeting = async () => {
    const now = Date.now();
    setMeetingStartTime(now);
    setCurrentEntryStart(now);
    setIsRunning(true);
    timerRef.current = setInterval(() => setElapsed(Date.now() - now), 1000);
    await updateDoc(doc(db, 'meetings', meetingId), { status: 'active', meetingStartTime: now, updatedAt: serverTimestamp() });
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const saveDraft = async () => {
    try {
      await updateDoc(doc(db, 'meetings', meetingId), { entries: entriesRef.current, status: 'active', updatedAt: serverTimestamp() });
      setLastSaved(new Date());
    } catch (err) { console.error('Auto-save failed:', err); }
  };

  const continueMeeting = async () => {
    const totalBreakMs = entries
      .filter(e => e.type === 'break')
      .reduce((sum, e) => sum + (e.endTime && e.startTime ? e.endTime - e.startTime : 0), 0);
    const adjustedStart = (meetingStartTime || Date.now()) + totalBreakMs;
    setMeeting(prev => ({ ...prev, status: 'active' }));
    setIsRunning(true);
    setIsPaused(false);
    timerRef.current = setInterval(() => setElapsed(Date.now() - adjustedStart), 1000);
    await updateDoc(doc(db, 'meetings', meetingId), { status: 'active', updatedAt: serverTimestamp() });
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const syncTimerToLastEntry = () => {
    const lastRealEntry = [...entries].reverse().find(e => e.type !== 'break');
    if (!lastRealEntry?.endTime || !meetingStartTime) return;
    const priorBreakMs = entries
      .filter(e => e.type === 'break' && e.endTime <= lastRealEntry.endTime)
      .reduce((sum, e) => sum + (e.endTime && e.startTime ? e.endTime - e.startTime : 0), 0);
    const targetElapsed = lastRealEntry.endTime - meetingStartTime - priorBreakMs;
    if (targetElapsed > 0) setElapsed(targetElapsed);
  };

  const pauseMeeting = () => {
    const now = Date.now();
    clearInterval(timerRef.current);
    setPauseTime(now);
    setIsPaused(true);
  };

  const resumeMeeting = () => {
    const breakEnd = Date.now();
    if (pauseTime) {
      const breakMarker = {
        id: `break-${breakEnd}`,
        type: 'break',
        startTime: pauseTime,
        endTime: breakEnd,
        text: '',
        speakerName: '',
        speakerDisplayName: '',
        speakerId: null,
        section: null,
        topic: currentTopic,
      };
      setEntries(prev => [...prev, breakMarker]);
    }
    setPauseTime(null);
    const frozenElapsed = elapsed;
    const resumeStart = breakEnd - frozenElapsed;
    timerRef.current = setInterval(() => setElapsed(Date.now() - resumeStart), 1000);
    setIsPaused(false);
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const addAttendee = async () => {
    const name = newAttendeeName.trim();
    if (!name) return;
    const newAttendee = { id: name, name, displayName: name, type: 'guest' };
    const updatedAttendees = [...(meeting.attendees || []), newAttendee];
    setMeeting(prev => ({ ...prev, attendees: updatedAttendees }));
    setNewAttendeeName('');
    setShowAddAttendee(false);
    try {
      await updateDoc(doc(db, 'meetings', meetingId), { attendees: updatedAttendees, updatedAt: serverTimestamp() });
    } catch (err) { console.error('Failed to save attendee:', err); }
  };

  const removeAttendee = async (attendeeId) => {
    const updatedAttendees = (meeting.attendees || []).filter(a => a.id !== attendeeId);
    setMeeting(prev => ({ ...prev, attendees: updatedAttendees }));
    try {
      await updateDoc(doc(db, 'meetings', meetingId), { attendees: updatedAttendees, updatedAt: serverTimestamp() });
    } catch (err) { console.error('Failed to remove attendee:', err); }
  };

  const endMeeting = async () => {
    if (!window.confirm('End the meeting and go to the inventory?')) return;
    setSaving(true);
    try {
      let finalEntries = [...entriesRef.current];
      if (currentText.trim()) finalEntries.push(buildEntry(currentText, null, currentTopic));
      clearInterval(timerRef.current);
      const meetingEndNow = Date.now();
      const totalDuration = meetingStartTime
        ? computeActiveDurationMs(finalEntries, meetingStartTime, meetingEndNow)
        : elapsed;
      const attendees = meeting?.attendees || [];
      const participantFeedback = buildParticipantFeedback(finalEntries, attendees);
      await updateDoc(doc(db, 'meetings', meetingId), {
        entries: finalEntries, status: 'completed',
        totalDuration, meetingEndTime: Date.now(),
        participantFeedback, updatedAt: serverTimestamp(),
      });
      navigate(`/meetings/${meetingId}/inventory`);
    } catch (err) {
      console.error('Failed to end meeting:', err);
      setSaving(false);
    }
  };

  function buildEntry(text, section, topic) {
    const matched = parseSpeaker(text, meeting?.attendees || []);
    const speaker = matched;
    return {
      id: Date.now().toString(),
      speakerName: speaker?.name || '',
      speakerDisplayName: getDisplayName(speaker) || speaker?.name || '',
      speakerId: speaker?.id || null,
      text: text.trim(),
      startTime: currentEntryStart || Date.now(),
      endTime: Date.now(),
      section: section || null,
      topic: topic || null,
    };
  }

  const handleLiveUpdate = useCallback((index, updated) => {
    setEntries(prev => prev.map((e, i) => i === index ? updated : e));
  }, []);

  const handleLiveDelete = useCallback((index) => {
    setEntries(prev => prev.filter((_, i) => i !== index));
  }, []);

  const commitEntry = useCallback(() => {
    const text = currentText.trim();
    if (!text) return;

    let finalTopic = currentTopic;
    const newTopic = extractTopic(text);
    if (newTopic) finalTopic = newTopic;

    const entry = buildEntry(text, null, finalTopic);
    const matched = parseSpeaker(text, meeting?.attendees || []);
    if (matched) setResolvedSpeaker(matched);

    setEntries(prev => [...prev, entry]);
    setCurrentTopic(finalTopic);
    setCurrentText('');
    setResolvedSpeaker(null);
    if (inputRef.current) inputRef.current.style.height = '';
    setCurrentEntryStart(Date.now());
    setShowMention(false);
    setTimeout(() => inputRef.current?.focus(), 0);
  }, [currentText, currentEntryStart, resolvedSpeaker, meeting, currentTopic]);

  // ── @mention ──────────────────────────────────────────────────────────────

  const attendees = meeting?.attendees || [];

  const mentionSuggestions = showMention
    ? (mentionQuery
        ? attendees.filter(a => getDisplayName(a).toLowerCase().includes(mentionQuery.toLowerCase()) || a.name.toLowerCase().includes(mentionQuery.toLowerCase()))
        : attendees)
    : [];

  const selectMention = useCallback((attendee) => {
    const tag = getDisplayName(attendee);
    setCurrentText(currentText.replace(/@[\w.]*$/, `@${tag}\u200B `));
    setResolvedSpeaker(attendee);
    setShowMention(false);
    setMentionIndex(0);
    setTimeout(() => inputRef.current?.focus(), 0);
  }, [currentText]);

  // ── Input handlers ────────────────────────────────────────────────────────

  const handleInputChange = (e) => {
    const val = e.target.value;
    setCurrentText(val);
    if (inputRef.current) {
      inputRef.current.style.height = 'auto';
      inputRef.current.style.height = inputRef.current.scrollHeight + 'px';
    }

    const mentionMatch = val.match(/@([\w.]*)$/);
    if (mentionMatch) {
      setMentionQuery(mentionMatch[1]);
      setShowMention(true);
      setMentionIndex(0);
      return;
    } else {
      setShowMention(false);
      setMentionQuery('');
    }
  };

  const handleKeyDown = (e) => {
    if (showMention && mentionSuggestions.length > 0) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setMentionIndex(i => Math.min(i + 1, mentionSuggestions.length - 1)); return; }
      if (e.key === 'ArrowUp') { e.preventDefault(); setMentionIndex(i => Math.max(i - 1, 0)); return; }
      if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); selectMention(mentionSuggestions[mentionIndex]); return; }
      if (e.key === 'Escape') { setShowMention(false); return; }
    }
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); commitEntry(); }
  };

  // ── Render ────────────────────────────────────────────────────────────────

  if (loading) return <PageLoading text="Loading meeting..." />;
  if (!meeting) return null;

  const isEditMode = ['completed', 'reviewed'].includes(meeting.status);
  if (isEditMode) {
    return <EditModeView meeting={meeting} entries={entries} setEntries={setEntries} navigate={navigate} meetingId={meetingId} onContinueMeeting={continueMeeting} />;
  }

  const detectedCodes = parseShortcodes(currentText);
  const currentSpeakerInText = parseSpeaker(currentText, attendees) || resolvedSpeaker;

  return (
    <div className={styles.pageLayout}>
      <div className={styles.mainColFlex}>
        {/* Header */}
        <div className={styles.header}>
          <div>
            <h2 className={styles.meetingTitle}>{meeting.name}</h2>
            <div className={styles.meetingMeta}>{meeting.teamName}</div>
          </div>
          <div className={styles.controls}>
            <div
              className={styles.timer}
              style={{ color: isRunning ? 'var(--olive)' : 'var(--ink-muted)' }}
            >
              {formatDuration(elapsed)}
            </div>
            <button className="shortcode-panel-toggle" onClick={() => setShowPanel(p => !p)}>📋 Shortcodes</button>
            {!isRunning ? (
              <button className="btn btn-primary" onClick={startMeeting}>▶ Start Meeting</button>
            ) : (
              <>
                <button className={`btn btn-secondary ${styles.btnSmFont}`} onClick={saveDraft}>Save Draft</button>
                <button className="btn btn-danger" onClick={endMeeting} disabled={saving}>
                  {saving ? <span className="spinner" /> : '⏹ End Meeting'}
                </button>
              </>
            )}
          </div>
        </div>

        {lastSaved && <div className={styles.autoSaved}>Auto-saved {lastSaved.toLocaleTimeString()}</div>}

        {/* Entry Feed */}
        <div className={styles.feedContainerLive}>
          <div className={styles.feedScroll}>
            {entries.length === 0 && !isRunning && (
              <div className={styles.emptyStateLg}>
                <div className={styles.emptyIcon}>⏱</div>
                <p style={{ fontWeight: 600, marginBottom: 4 }}>Press Start Meeting to begin</p>
                <p className={styles.emptyStateSm}>Then type <strong>@name</strong> to tag a speaker and <strong>Enter</strong> to commit each entry.</p>
              </div>
            )}
            {entries.length === 0 && isRunning && (
              <div className={styles.emptyState}>
                <p className={styles.emptyStateSm}>Type <strong>@name</strong> to tag the speaker, add notes and shortcodes, then hit <strong>Enter</strong>. Click any entry to edit it.</p>
              </div>
            )}
            {entries.map((entry, i) => (
              entry.type === 'break'
                ? <BreakMarkerRow key={entry.id} entry={entry} />
                : <LiveEntryRow
                    key={entry.id}
                    entry={entry}
                    index={i}
                    attendees={attendees}
                    onUpdate={handleLiveUpdate}
                    onDelete={handleLiveDelete}
                  />
            ))}
            {isPaused && (
              <div style={{ margin: '0 12px 12px', background: 'var(--bg-card)', border: '1.5px solid var(--warning)', borderRadius: 8, padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                  <span style={{ color: 'var(--warning)', fontWeight: 600, fontSize: '0.9rem' }}>⏸ Break in progress</span>
                  {entries.some(e => e.type !== 'break') && (
                    <button
                      className="btn btn-secondary"
                      style={{ padding: '4px 10px', fontSize: '0.78rem' }}
                      onClick={syncTimerToLastEntry}
                      title="Reset elapsed time to match the last recorded entry"
                    >
                      Sync timer to last entry
                    </button>
                  )}
                </div>
                {(meeting.attendees || []).length > 0 && (
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {(meeting.attendees || []).map(a => (
                      <span key={a.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 10px', background: 'var(--bg-soft)', border: '1px solid var(--border)', borderRadius: 12, fontSize: '0.82rem', color: 'var(--ink)' }}>
                        {getDisplayName(a)}
                        <button
                          onClick={() => removeAttendee(a.id)}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-muted)', fontSize: '0.85rem', padding: '0 0 0 2px', lineHeight: 1, display: 'flex', alignItems: 'center' }}
                          title={`Remove ${getDisplayName(a)}`}
                        >
                          ✕
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  {showAddAttendee ? (
                    <>
                      <input
                        autoFocus
                        type="text"
                        value={newAttendeeName}
                        onChange={e => setNewAttendeeName(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') addAttendee(); if (e.key === 'Escape') { setShowAddAttendee(false); setNewAttendeeName(''); } }}
                        placeholder="Full name"
                        style={{ padding: '6px 12px', border: '1.5px solid var(--border)', borderRadius: 6, fontSize: '0.9rem', fontFamily: 'var(--font-body)' }}
                      />
                      <button className="btn btn-primary" style={{ padding: '6px 14px', fontSize: '0.85rem' }} onClick={addAttendee} disabled={!newAttendeeName.trim()}>Add</button>
                      <button className="btn btn-secondary" style={{ padding: '6px 10px', fontSize: '0.85rem' }} onClick={() => { setShowAddAttendee(false); setNewAttendeeName(''); }}>Cancel</button>
                    </>
                  ) : (
                    <button className="btn btn-secondary" style={{ padding: '6px 12px', fontSize: '0.82rem' }} onClick={() => setShowAddAttendee(true)}>+ Add attendee</button>
                  )}
                </div>
              </div>
            )}
          </div>

          {entries.length > 0 && <div className={styles.feedDivider} />}

          {isRunning && (
            <div className={styles.inputArea}>
              {currentSpeakerInText && (
                <div className={styles.speakerPreview}>
                  <div
                    className={styles.speakerPreviewAvatar}
                    style={{ background: avatarColor(currentSpeakerInText.name) }}
                  >
                    {getDisplayName(currentSpeakerInText)?.charAt(0)?.toUpperCase()}
                  </div>
                  <span
                    className={styles.speakerPreviewName}
                    style={{ color: avatarColor(currentSpeakerInText.name) }}
                  >
                    {getDisplayName(currentSpeakerInText)}
                  </span>
                  <span className={styles.speakerLabel}>speaking</span>
                </div>
              )}

              {showMention && mentionSuggestions.length > 0 && (
                <MentionDropdown suggestions={mentionSuggestions} activeIndex={mentionIndex} onSelect={selectMention} />
              )}

              <textarea
                ref={inputRef}
                rows={1}
                value={currentText}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                disabled={!isRunning}
                placeholder='@name notes... /m /c comment `w #Topic# → Enter'
                className={styles.mainInput}
                style={{ resize: 'none', overflow: 'hidden' }}
              />

              {detectedCodes.length > 0 && (
                <div className={styles.detectedCodes}>
                  <span className={styles.detectedLabel}>Detected:</span>
                  {detectedCodes.map((c, i) => <TagChip key={i} code={c.code} def={c.definition} />)}
                </div>
              )}
            </div>
          )}
        </div>

        {entries.length > 0 && (
          <div className={styles.entryCount}>
            {entries.filter(e => e.type !== 'break').length} entr{entries.filter(e => e.type !== 'break').length !== 1 ? 'ies' : 'y'} recorded
          </div>
        )}
      </div>

      {isRunning && (
        <button
          className={isPaused ? 'btn btn-primary' : 'btn btn-secondary'}
          onClick={isPaused ? resumeMeeting : pauseMeeting}
          style={{
            position: 'fixed', bottom: 28, left: '50%', transform: 'translateX(-50%)',
            zIndex: 100, boxShadow: '0 4px 16px rgba(0,0,0,0.18)',
            padding: '10px 28px', fontSize: '0.9rem', fontWeight: 600,
          }}
        >
          {isPaused ? '▶ Resume Meeting' : '⏸ Take a Break'}
        </button>
      )}

      <div ref={panelAnchorRef} className={styles.panelAnchor}>
        <ShortcodePanel currentTopic={currentTopic} mobileOpen={showPanel} onClose={() => setShowPanel(false)} fixedLeft={panelLeft} />
      </div>
    </div>
  );
}
